import React, { useState, useEffect, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import {
    LayoutDashboard,
    FileText,
    Layout,
    BookOpen,
    Users,
    Shield,
    Settings,
    User,
    LogOut,
    ArrowRightLeft,
    Activity,
    FileEdit,
    Wifi,
    WifiOff,
    Gamepad2,
    Signal,
    Network,
    Bluetooth,
    Inbox,
    PhoneCall
} from 'lucide-react';
import modulesService, { Module } from '../../services/modules.service';
import styles from './Sidebar.module.css';
import authService from '../../services/auth.service';

interface SidebarProps {
    onCollapseChange?: (collapsed: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ onCollapseChange }) => {
    const [collapsed, setCollapsed] = useState(false);
    const [modules, setModules] = useState<Module[]>([]);
    const [loading, setLoading] = useState(true);
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [showOfflinePrompt, setShowOfflinePrompt] = useState(false);
    const [connType, setConnType] = useState<{ type: string; effectiveType: string }>(() => {
        const conn = (navigator as any).connection;
        return { type: conn?.type ?? 'unknown', effectiveType: conn?.effectiveType ?? '4g' };
    });
    const [hoveredModule, setHoveredModule] = useState<{ module: Module; top: number } | null>(null);
    const sidebarRef = React.useRef<HTMLDivElement>(null);
    const eggRef = useRef<{ count: number; timer: ReturnType<typeof setTimeout> | null }>({ count: 0, timer: null });

    const handleVersionClick = () => {
        eggRef.current.count += 1;
        if (eggRef.current.timer) clearTimeout(eggRef.current.timer);
        eggRef.current.timer = setTimeout(() => { eggRef.current.count = 0; }, 600);
        if (eggRef.current.count >= 3) {
            eggRef.current.count = 0;
            window.dispatchEvent(new CustomEvent('docucr:snake'));
        }
    };

    // Icon mapping
    const iconMap: Record<string, React.ReactNode> = {
        LayoutDashboard: <LayoutDashboard size={20} />,
        FileText: <FileText size={20} />,
        Layout: <Layout size={20} />,
        BookOpen: <BookOpen size={20} />,
        Users: <Users size={20} />,
        Shield: <Shield size={20} />,
        Settings: <Settings size={20} />,
        User: <User size={20} />,
        Activity: <Activity size={20} />,
        FileEdit: <FileEdit size={20} />,
        inbox: <Inbox size={20} />,
        PhoneCall: <PhoneCall size={20} />
    };

    useEffect(() => {
        loadUserModules();
        // Show bubble on mount if already offline
        if (!navigator.onLine) setShowOfflinePrompt(true);
    }, []);

    // Clean up egg timer on unmount
    useEffect(() => {
        return () => {
            if (eggRef.current.timer) clearTimeout(eggRef.current.timer);
        };
    }, []);

    useEffect(() => {
        const handleOnline = () => {
            setIsOnline(true);
            setShowOfflinePrompt(false);
        };
        const handleOffline = () => {
            setIsOnline(false);
            setShowOfflinePrompt(true);
        };
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        const conn = (navigator as any).connection;
        const updateConn = () =>
            setConnType({ type: conn?.type ?? 'unknown', effectiveType: conn?.effectiveType ?? '4g' });
        conn?.addEventListener('change', updateConn);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
            conn?.removeEventListener('change', updateConn);
        };
    }, []);

    const getConnectionInfo = (size: number): { icon: React.ReactNode; label: string; color: string; textColor: string } => {
        if (!isOnline) return { icon: <WifiOff size={size} color="#b91c1c" />, label: 'Offline', color: '#b91c1c', textColor: '#b91c1c' };
        const color = '#22c55e';
        const textColor = '#22c55e';
        const { type, effectiveType } = connType;
        switch (type) {
            case 'wifi':
                return { icon: <Wifi size={size} color={color} />, label: 'Online', color, textColor };
            case 'cellular':
            case 'wimax':
                return { icon: <Signal size={size} color={color} />, label: 'Online', color, textColor };
            case 'ethernet':
                return { icon: <Network size={size} color={color} />, label: 'Online', color, textColor };
            case 'bluetooth':
                return { icon: <Bluetooth size={size} color={color} />, label: 'Online', color, textColor };
            default:
                if (effectiveType === '2g' || effectiveType === 'slow-2g')
                    return { icon: <Signal size={size} color={color} />, label: 'Online', color, textColor };
                if (effectiveType === '3g')
                    return { icon: <Signal size={size} color={color} />, label: 'Online', color, textColor };
                return { icon: <Wifi size={size} color={color} />, label: 'Online', color, textColor };
        }
    };

    const handlePlaySnake = () => {
        setShowOfflinePrompt(false);
        window.dispatchEvent(new CustomEvent('docucr:snake'));
    };

    const HIDDEN_MODULE_ROUTES = new Set(['/patient-calling']);

    const loadUserModules = async () => {
        try {
            const user = authService.getUser();
            if (user?.email) {
                const userModules = await modulesService.getUserModules(user.email);
                setModules(userModules.filter(m => !HIDDEN_MODULE_ROUTES.has(m.route)));
            }
        } catch (error) {
            console.error('Failed to load user modules:', error);
        } finally {
            setLoading(false);
        }
    };

    const toggleCollapse = () => {
        const newCollapsed = !collapsed;
        setCollapsed(newCollapsed);
        onCollapseChange?.(newCollapsed);
        if (newCollapsed) setShowOfflinePrompt(false);
    };

    const getModulesByCategory = () => {
        return modulesService.getModulesByCategory(modules);
    };

    const renderModuleItem = (module: Module) => {
        const icon = iconMap[module.icon] || <FileText size={20} />;

        return (
            <NavLink
                key={module.id}
                to={module.route}
                className={({ isActive }) => `${styles.navItem} ${isActive ? styles.active : ''}`}
                onMouseEnter={(e) => {
                    if (collapsed && sidebarRef.current) {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const sidebarRect = sidebarRef.current.getBoundingClientRect();
                        setHoveredModule({
                            module,
                            top: rect.top - sidebarRect.top
                        });
                    }
                }}
                onMouseLeave={() => {
                    if (collapsed) {
                        setHoveredModule(null);
                    }
                }}
            >
                <div className={styles.navIcon}>{icon}</div>
                <span className={styles.navLabel}>{module.label}</span>
            </NavLink>
        );
    };

    const renderCategorySection = (category: string, categoryModules: Module[]) => {
        if (categoryModules.length === 0) return null;

        const categoryLabels: Record<string, string> = {
            main: 'Main',
            admin: 'Administration',
            user: 'User'
        };

        return (
            <div key={category}>
                {!collapsed && category !== 'main' && (
                    <div className={styles.navGroupLabel}>{categoryLabels[category] || category}</div>
                )}
                {categoryModules.map(renderModuleItem)}
            </div>
        );
    };

    if (loading) {
        return (
            <div className={`${styles.sidebar} ${collapsed ? styles.collapsed : styles.expanded}`}>
                <div className={styles.header}>
                    <div className={styles.logoContainer}>
                        <div className={styles.logoIcon}>
                            <ArrowRightLeft size={18} />
                        </div>
                        {!collapsed && <span className="brand-font">docucr</span>}
                    </div>
                </div>
                <div className={styles.nav}>
                    <div className={styles.loading}>
                        <div className={styles.spinner}></div>
                        {!collapsed && <span>Loading...</span>}
                    </div>
                </div>
            </div>
        );
    }

    const modulesByCategory = getModulesByCategory();

    return (
        <div
            ref={sidebarRef}
            className={`${styles.sidebar} ${collapsed ? styles.collapsed : styles.expanded}`}
        >
            {/* Header */}
            <div className={styles.header}>
                {collapsed ? (
                    <button className={styles.collapseBtn} onClick={toggleCollapse}>
                        <LogOut size={16} />
                    </button>
                ) : (
                    <>
                        <div className={styles.logoContainer}>
                            <div className={styles.logoIcon}>
                                d
                            </div>
                            <span className="brand-font">docucr</span>
                        </div>
                        <button className={styles.collapseBtn} onClick={toggleCollapse}>
                            <LogOut size={16} style={{ transform: 'rotate(180deg)' }} />
                        </button>
                    </>
                )}
            </div>

            {/* Navigation */}
            <div className={styles.nav}>
                {modules.length === 0 && !loading && (
                    <div style={{
                        padding: '24px 16px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#6b7280',
                        fontSize: '13px',
                        textAlign: 'center'
                    }}>
                        <Shield size={24} style={{ marginBottom: collapsed ? '0' : '8px', opacity: 0.5 }} />
                        {!collapsed && <div>No modules assigned</div>}
                    </div>
                )}

                {/* Main modules */}
                {renderCategorySection('main', modulesByCategory.main || [])}

                {/* Admin modules (includes Support Admin if superuser) */}
                {renderCategorySection('admin', modulesByCategory.admin || [])}

                {/* User modules */}
                {renderCategorySection('user', modulesByCategory.user || [])}
            </div>

            {/* Footer */}
            <div className={styles.footer}>
                <div className={styles.footerDivider} />
                {(() => {
                    const { icon: connIcon, label: connLabel, color: connColor, textColor: connTextColor } = getConnectionInfo(collapsed ? 14 : 13);
                    return collapsed ? (
                        <div className={styles.footerNetworkIcon} title={connLabel} style={{ color: connColor }}>
                            {!isOnline && !showOfflinePrompt && (
                                <button className={styles.snakeHintBtn} title="Play snake!" onClick={handlePlaySnake}>
                                    <Gamepad2 size={13} />
                                </button>
                            )}
                            {connIcon}
                        </div>
                    ) : (
                        <div className={styles.footerContent}>
                            <span className={styles.footerVersion} onClick={handleVersionClick} style={{ cursor: 'default' }}>v0.1.0</span>
                            <div className={styles.footerNetworkStatus} title={connLabel} style={{ color: connColor }}>
                                {!isOnline && !showOfflinePrompt && (
                                    <button className={styles.snakeHintBtn} title="Play snake!" onClick={handlePlaySnake}>
                                        <Gamepad2 size={13} />
                                    </button>
                                )}
                                {connIcon}
                                <span className={styles.footerNetworkLabel} style={{ color: connTextColor }}>{connLabel}</span>
                            </div>
                        </div>
                    );
                })()}
            </div>

            {/* Offline Bubble — expanded mode only */}
            {showOfflinePrompt && !collapsed && (
                <div className={styles.offlineBubble}>
                    <span className={styles.offlineBubbleText}>bored? wanna play?</span>
                    <div className={styles.offlineBubbleActions}>
                        <button className={styles.offlineBubbleYes} onClick={handlePlaySnake}>yes!</button>
                        <button className={styles.offlineBubbleNo} onClick={() => setShowOfflinePrompt(false)}>nah</button>
                    </div>
                    <div className={styles.offlineBubbleTail} />
                </div>
            )}

            {/* Hover Panel for Collapsed Mode */}
            {collapsed && hoveredModule && (
                <div
                    className={styles.hoverPanel}
                    style={{ top: hoveredModule.top }}
                >
                    <div className={styles.hoverPanelContent}>
                        <div className={styles.hoverIcon}>
                            {iconMap[hoveredModule.module.icon] || <FileText size={20} />}
                        </div>
                        <span className={styles.hoverLabel}>{hoveredModule.module.label}</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Sidebar;